import { NextRouter } from 'next/router';

import { validateMfaToken } from '../lib/api';
import { CognitoUser } from '../types/user';
import { mfaTokenPrefix } from '../constants';

export const routerSingleParam = (param?: string | string[]) =>
  typeof param === 'string'
    ? param
    : Array.isArray(param)
    ? param[0]
    : undefined;

export function urlSafeDecode(hex: string) {
  if (hex && hex.match(/.{2}/g))
    return (hex.match(/.{2}/g) as Array<string>)
      .map(function (char: string) {
        return String.fromCharCode(parseInt(char, 16));
      })
      .join('');
  return hex || '';
}

export const getUserPoolClientData = (router: NextRouter) => {
  const WebClients =
    ((process.env.userPoolWebClientIds as unknown) as Record<
      string,
      Record<string, string>
    >) || {};

  const cookieStorage =
    ((process.env.cookieStorage as unknown) as Record<string, string>) || {};

  const getTenancyNProduct = (clientId: string) => [
    WebClients[clientId] ? WebClients[clientId].tenancy : '',
    WebClients[clientId] && WebClients[clientId].product
      ? WebClients[clientId].product
      : '',
    WebClients[clientId].useCookieStorage ? cookieStorage.sameSite : null,
  ];
  if (typeof window !== 'undefined') {
    if (router && router.asPath && router.asPath.includes('client_id')) {
      if (router && router.query && router.query.client_id) {
        const clientId = router?.query?.client_id as string;
        const clientRedirect =
          (router?.query?.redirect_uri as string) ||
          (router?.query?.redirect_url as string) ||
          (WebClients[clientId] ? WebClients[clientId].redirectUrls[0] : '');
        const clientState = router?.query?.state as string;
        if (!WebClients[clientId].redirectUrls.includes(clientRedirect))
          throw new Error('Bad Request');
        localStorage.setItem('clientId', clientId);
        localStorage.setItem('clientRedirect', clientRedirect);
        localStorage.setItem('clientState', clientState);
        return [
          clientId,
          clientRedirect,
          ...getTenancyNProduct(clientId),
          clientState,
        ];
      } else {
        return [];
      }
    } else if (
      router.asPath &&
      router.asPath.includes('#') &&
      (router.asPath.includes('id_token') ||
        router.asPath.includes('error_description'))
    ) {
      const clientId = localStorage.getItem('clientId') || '';
      const clientRedirect = localStorage.getItem('clientRedirect') || '';
      if (!WebClients[clientId]?.redirectUrls?.includes(clientRedirect))
        return [];
      return clientId
        ? [
            clientId,
            clientRedirect,
            ...getTenancyNProduct(clientId as string),
            localStorage.getItem('clientState'),
          ]
        : [];
    }
  }
  const clientIdRegex = /client_id=([^&]*)/;
  const redirectURLRegex = /redirect_url=([^&]*)/;

  const redirectUrlMatch = router.asPath.match(redirectURLRegex);
  const clientIdMatch = router.asPath.match(clientIdRegex);

  const redirectUrl = decodeURIComponent(redirectUrlMatch?.[1] || '');
  const clientId = clientIdMatch?.[1] || '';

  if (clientId) {
    const redirectURLs: Array<string> = WebClients[clientId as string]
      ? ((WebClients[clientId as string].redirectUrls as unknown) as Array<
          string
        >)
      : [];

    const isRedirectURLMatched = redirectURLs.includes(redirectUrl);

    if (isRedirectURLMatched) {
      localStorage.setItem('clientId', clientId);
      localStorage.setItem('clientRedirect', redirectUrl);

      return [clientId, redirectUrl, ...getTenancyNProduct(clientId)];
    }
  }

  const defaultClientId = process.env.defaultClientId!;
  const defaultRedirect = WebClients[defaultClientId as string]
    ? WebClients[defaultClientId as string].redirectUrls[0]
    : '';

  return [
    defaultClientId,
    defaultRedirect,
    ...getTenancyNProduct(defaultClientId),
  ];
};

export const requireMFASetup = (user: CognitoUser) => {
  if (
    user.attributes['custom:mfaRequirement'] &&
    !user.attributes['custom:MFAType']
  ) {
    return true;
  }
  return false;
};

export const requireMFAValidationOld = async (
  user: CognitoUser,
  mfaToken: Record<'key' | 'value', string | undefined>
) => {
  const {
    idToken: { jwtToken: idToken },
  } = user.signInUserSession;
  const clientIdRegex = new RegExp(`${mfaTokenPrefix}.(.*).token`);
  if (user.attributes['custom:MFAType']) {
    const mfaData = {
      enabled: true,
      type: user.attributes['custom:MFAType'],
      ...(mfaToken.value && clientIdRegex.test(mfaToken.key || '')
        ? {
            token: mfaToken.value,
            clientId: (mfaToken.key || '').match(clientIdRegex)![1],
          }
        : {}),
    };

    if (mfaData.token) {
      const resp = await validateMfaToken(
        mfaData.token,
        idToken,
        mfaData.clientId || ''
      );
      if (resp && resp.result === 'success') {
        return false; // MFA validation dont in past 30 days
      } else {
        return true; // MFA validation has expired or invalid token
      }
    } else {
      return true; // MFA validation missing
    }
  }
  return false; // No MFA validation required
};

export const requireMFAValidation = async (
  user: CognitoUser,
  mfaToken: string | undefined,
  clientId: string
) => {
  const {
    idToken: { jwtToken: idToken },
  } = user.signInUserSession;
  if (user.attributes['custom:MFAType']) {
    const mfaData = {
      enabled: true,
      type: user.attributes['custom:MFAType'],
      ...(mfaToken
        ? {
            token: mfaToken,
          }
        : {}),
    };

    if (mfaData.token) {
      const resp = await validateMfaToken(mfaData.token, idToken, clientId);

      if (resp && resp.result === 'success') {
        return false; // MFA validation done in past 30 days
      }
      return true; // MFA validation has expired or invalid token
    }
    return true; // MFA validation missing
  }
  return false; // No MFA validation required
};
